/**
 * Reglas de negocio puras para PO (Purchase Order) - modelo VD §4.11 + §5.4.
 * Sin dependencias de UI ni backend - facilmente testeables.
 */

import type { Po, PoLinea, Pr, PrLinea } from "@app-types/entities";
import type { EstadoPo } from "@constants/estados";
import { TRANSICIONES_PO, puedeTransicionar } from "@constants/estados";

/** Valida si la transicion de estado es permitida (VD §5.4). */
export function puedeTransicionarPo(actual: EstadoPo, destino: EstadoPo): boolean {
  return puedeTransicionar(actual, destino, TRANSICIONES_PO);
}

// ============================================================================
// Aclaraciones técnicas de la PO (P4)
// ============================================================================

/**
 * `true` si la PO tiene alguna aclaración (general o por línea).
 *
 * Usado por la UI para mostrar el indicador ✏️ en el tab del detalle y en el
 * listado, de modo que el comprador detecte de un vistazo qué POs tienen
 * anotaciones técnicas (sustituciones, condiciones especiales, etc.).
 */
export function tieneAclaraciones(po: Po): boolean {
  if (po.aclaracionGeneral && po.aclaracionGeneral.trim().length > 0) return true;
  return (po.lineas ?? []).some(
    (l) => l.aclaracionEspec && l.aclaracionEspec.trim().length > 0,
  );
}

// ============================================================================
// Líneas de la PO (sub-detalle § P1)
// ============================================================================

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Suma del valor total de las líneas de la PO. */
export function calcularMontoLineasPo(lineas: PoLinea[]): number {
  return lineas.reduce((s, l) => s + l.valorTotal, 0);
}

/** Recalcula valor_total = cantidad × valor_unit en cada línea. */
export function recalcularLineasPo(lineas: PoLinea[]): PoLinea[] {
  return lineas.map((l) => ({ ...l, valorTotal: round2(l.cantidad * l.valorUnit) }));
}

// ============================================================================
// Cruce PR ↔ POs: ¿se adjudicó toda la PR?
// ============================================================================

/**
 * Estado de adjudicación de UNA línea de PR.
 *
 *   - "NO_ADJUDICADA": ninguna PO derivada referencia esta línea.
 *   - "PARCIAL":       Σ(POs.cantidad) < PR.cantidad.
 *   - "COMPLETA":      Σ(POs.cantidad) === PR.cantidad.
 *   - "SOBRE_ADJUDICADA": Σ(POs.cantidad) > PR.cantidad. Anomalía: indica
 *     un error de digitación o que se adjudicó más de lo solicitado. La UI
 *     debe alertar.
 */
export type EstadoAdjudicacionLinea =
  | "NO_ADJUDICADA"
  | "PARCIAL"
  | "COMPLETA"
  | "SOBRE_ADJUDICADA";

export interface AdjudicacionLinea {
  /** Línea de PR original. */
  prLinea: PrLinea;
  /** Suma de cantidad adjudicada en POs no canceladas. */
  cantidadAdjudicada: number;
  /** Pendiente = max(0, prLinea.cantidad - cantidadAdjudicada). */
  pendiente: number;
  /** Diferencias positivas (sobre-adjudicación). 0 si no aplica. */
  excedente: number;
  estado: EstadoAdjudicacionLinea;
  /**
   * POs que cubren esta línea (sin las canceladas). Útil para
   * mostrar quién está adjudicando qué.
   */
  poRefs: Array<{ poId: string; poCodigo: string; cantidad: number; valorTotal: number }>;
}

export interface ResumenAdjudicacion {
  porLinea: AdjudicacionLinea[];
  /** Líneas agregadas en POs sin `prLineaId` (no corresponden a una PR.linea). */
  lineasAgregadasEnPo: Array<{
    poId: string;
    poCodigo: string;
    linea: PoLinea;
  }>;
  /** Estado global agregado de la PR. */
  estadoGlobal:
    | "SIN_LINEAS_PR"
    | "NO_ADJUDICADA"
    | "PARCIAL"
    | "COMPLETA"
    | "SOBRE_ADJUDICADA";
  /** Monto total de la PR según líneas. */
  totalPedidoPr: number;
  /** Monto total adjudicado en POs no canceladas (de líneas con prLineaId). */
  totalAdjudicadoPr: number;
  /** Monto de líneas agregadas en POs (sin prLineaId). */
  totalAgregadoEnPo: number;
}

/**
 * Cruza la PR contra sus POs derivadas para calcular qué se adjudicó por
 * línea. POs canceladas se excluyen del cómputo (no comprometen monto).
 *
 * Casos cubiertos:
 *   - Multi-source: una PR-línea puede partirse en varias POs (proveedor A
 *     adjudica 60 unidades, proveedor B adjudica 40). El helper suma todo.
 *   - Líneas agregadas en PO sin contraparte en PR (`prLineaId === undefined`)
 *     se devuelven aparte en `lineasAgregadasEnPo` — son ítems extra ofrecidos
 *     por el proveedor (sustituto, accesorio, complemento).
 *
 * Es función pura — no toca DB ni hooks. Testeable en aislamiento.
 */
export function analizarAdjudicacionPr(
  pr: Pr,
  pos: Po[],
): ResumenAdjudicacion {
  // 1. POs derivadas vivas (no canceladas, ligadas a esta PR).
  const posDerivadas = pos.filter(
    (p) => p.prId === pr.id && p.estado !== "CANCELADA",
  );

  // 2. Índice de adjudicaciones por prLineaId.
  const porPrLineaId = new Map<
    string,
    Array<{ poId: string; poCodigo: string; cantidad: number; valorTotal: number }>
  >();
  const lineasAgregadasEnPo: ResumenAdjudicacion["lineasAgregadasEnPo"] = [];

  for (const po of posDerivadas) {
    for (const linea of po.lineas ?? []) {
      if (linea.prLineaId) {
        const arr = porPrLineaId.get(linea.prLineaId) ?? [];
        arr.push({
          poId: po.id,
          poCodigo: po.codigo,
          cantidad: linea.cantidad,
          valorTotal: linea.valorTotal,
        });
        porPrLineaId.set(linea.prLineaId, arr);
      } else {
        lineasAgregadasEnPo.push({
          poId: po.id,
          poCodigo: po.codigo,
          linea,
        });
      }
    }
  }

  // 3. Para cada PR.linea calcular su estado de adjudicación.
  const porLinea: AdjudicacionLinea[] = pr.lineas.map((prLinea) => {
    const poRefs = porPrLineaId.get(prLinea.id) ?? [];
    const cantidadAdjudicada = poRefs.reduce((s, r) => s + r.cantidad, 0);
    const pendiente = Math.max(0, prLinea.cantidad - cantidadAdjudicada);
    const excedente = Math.max(0, cantidadAdjudicada - prLinea.cantidad);
    let estado: EstadoAdjudicacionLinea;
    if (cantidadAdjudicada === 0) estado = "NO_ADJUDICADA";
    else if (excedente > 0) estado = "SOBRE_ADJUDICADA";
    else if (pendiente > 0) estado = "PARCIAL";
    else estado = "COMPLETA";
    return {
      prLinea,
      cantidadAdjudicada,
      pendiente,
      excedente,
      estado,
      poRefs,
    };
  });

  // 4. Agregados globales.
  const totalPedidoPr = pr.lineas.reduce((s, l) => s + l.valorTotal, 0);
  const totalAdjudicadoPr = porLinea.reduce(
    (s, a) => s + a.poRefs.reduce((ss, r) => ss + r.valorTotal, 0),
    0,
  );
  const totalAgregadoEnPo = lineasAgregadasEnPo.reduce(
    (s, x) => s + x.linea.valorTotal,
    0,
  );

  let estadoGlobal: ResumenAdjudicacion["estadoGlobal"];
  if (pr.lineas.length === 0) {
    estadoGlobal = "SIN_LINEAS_PR";
  } else if (porLinea.every((a) => a.estado === "NO_ADJUDICADA")) {
    estadoGlobal = "NO_ADJUDICADA";
  } else if (porLinea.some((a) => a.estado === "SOBRE_ADJUDICADA")) {
    estadoGlobal = "SOBRE_ADJUDICADA";
  } else if (porLinea.every((a) => a.estado === "COMPLETA")) {
    estadoGlobal = "COMPLETA";
  } else {
    estadoGlobal = "PARCIAL";
  }

  return {
    porLinea,
    lineasAgregadasEnPo,
    estadoGlobal,
    totalPedidoPr,
    totalAdjudicadoPr,
    totalAgregadoEnPo,
  };
}

/**
 * Calcula la fecha de entrega en base a fechaEmision + plazoEntregaDias (VD §A5).
 * Devuelve formato YYYY-MM-DD.
 */
export function calcularFechaEntrega(fechaEmision: string, plazoDias: number): string {
  if (!fechaEmision || !Number.isFinite(plazoDias) || plazoDias < 0) return "";
  const d = new Date(fechaEmision + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + plazoDias);
  return d.toISOString().slice(0, 10);
}

/** Dias restantes para la entrega calculada. Negativo si ya pasó. */
export function diasParaEntrega(po: Pick<Po, "fechaEntregaCalc">, hoy = new Date()): number {
  if (!po.fechaEntregaCalc) return 0;
  const f = new Date(po.fechaEntregaCalc + "T00:00:00");
  if (Number.isNaN(f.getTime())) return 0;
  return Math.ceil((f.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
}

/** Valida los datos del PO. */
export function validarPo(
  p: Pick<
    Po,
    | "codigo"
    | "afeId"
    | "costeCodigo"
    | "descripcion"
    | "proveedorNombre"
    | "fechaEmision"
    | "plazoEntregaDias"
    | "fechaVigenciaFin"
    | "monto"
  >,
): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (!p.codigo?.trim()) errores.push("Código es obligatorio");
  if (!p.afeId) errores.push("AFE asociado es obligatorio");
  if (!p.costeCodigo) errores.push("Clase de coste es obligatoria");
  if (!p.descripcion?.trim()) errores.push("Descripción es obligatoria");
  if (!p.proveedorNombre?.trim()) errores.push("Proveedor es obligatorio");
  if (!p.fechaEmision) errores.push("Fecha de emisión es obligatoria");
  if (p.plazoEntregaDias === undefined || p.plazoEntregaDias <= 0) {
    errores.push("Plazo de entrega (días) debe ser mayor a cero");
  }
  if (!p.fechaVigenciaFin) errores.push("Fecha de vigencia fin es obligatoria");
  if (
    p.fechaEmision &&
    p.fechaVigenciaFin &&
    p.fechaVigenciaFin < p.fechaEmision
  ) {
    errores.push("Vigencia fin debe ser posterior a la emisión");
  }
  if (!p.monto || p.monto <= 0) errores.push("Monto debe ser mayor a cero");
  return { valido: errores.length === 0, errores };
}

/**
 * PRs que pueden ser origen de una PO nueva (estado APROBADA, ET_FAC, EE_ABAS o
 * ADJ_PARCIAL si admite múltiples POs).
 */
export function prsAdjudicables(prs: Pr[], monedaPo: string, prIdActual?: string): Pr[] {
  return prs.filter(
    (p) =>
      p.moneda === monedaPo &&
      (p.estado === "APROBADA" ||
        p.estado === "ET_FAC" ||
        p.estado === "EE_ABAS" ||
        p.estado === "ADJ_PARCIAL" ||
        p.id === prIdActual),
  );
}

// ============================================================================
// Análisis de tiempos de entrega (para reporte de estadísticas)
// ============================================================================

/**
 * Estado de entrega derivado del cumplimiento del plazo contractual.
 *  - "a_tiempo": PO entregada y diasReales ≤ plazoEntregaDias
 *  - "atrasada": PO entregada (o pagada) pero diasReales > plazoEntregaDias
 *  - "vencida": PO en estado VENCIDA (plazo pasó sin entrega)
 *  - "en_curso": PO EMITIDA o EN_ESPERA_ENTREGA (proceso vivo, plazo no terminado)
 *  - "cancelada": PO CANCELADA (no se evalúa contra plazo)
 */
export type EstadoEntregaPo =
  | "a_tiempo"
  | "atrasada"
  | "vencida"
  | "en_curso"
  | "cancelada";

/**
 * Calcula días reales de entrega (fechaEntregaReal - fechaEmision).
 * Devuelve null si la PO no está entregada/pagada.
 */
export function diasRealesEntrega(po: Po): number | null {
  if (!po.fechaEntregaReal || !po.fechaEmision) return null;
  const a = new Date(po.fechaEmision + "T00:00:00").getTime();
  const b = new Date(po.fechaEntregaReal + "T00:00:00").getTime();
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

/** Días entre entrega y pago (cuando ambos existen). */
export function diasEntregaAPago(po: Po): number | null {
  if (!po.fechaEntregaReal || !po.fechaPago) return null;
  const a = new Date(po.fechaEntregaReal + "T00:00:00").getTime();
  const b = new Date(po.fechaPago + "T00:00:00").getTime();
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

/** Devuelve el estado de entrega del PO según su estado y tiempos. */
export function estadoEntregaPo(po: Po): EstadoEntregaPo {
  if (po.estado === "CANCELADA") return "cancelada";
  if (po.estado === "VENCIDA") return "vencida";
  if (po.estado === "ENTREGADA" || po.estado === "PAGADA") {
    const dias = diasRealesEntrega(po);
    if (dias === null) return "a_tiempo"; // sin fechas no se puede comparar
    return dias <= po.plazoEntregaDias ? "a_tiempo" : "atrasada";
  }
  return "en_curso";
}

/**
 * Detecta si una PO debería estar VENCIDA: estado es EMITIDA o
 * EN_ESPERA_ENTREGA, y la fechaEntregaCalc ya pasó (vs hoy) sin
 * fechaEntregaReal.
 *
 * Este helper se usa para mostrar visualmente el "deber ser" sin tocar la BD;
 * la función `promoverPosVencidas()` (en poQueries) puede aplicar el cambio
 * efectivo si el usuario lo confirma.
 */
export function deberiaEstarVencida(po: Po, hoy: Date = new Date()): boolean {
  if (po.estado !== "EMITIDA" && po.estado !== "EN_ESPERA_ENTREGA") return false;
  if (po.fechaEntregaReal) return false;
  if (!po.fechaEntregaCalc) return false;
  const fc = new Date(po.fechaEntregaCalc + "T00:00:00").getTime();
  if (isNaN(fc)) return false;
  return fc < hoy.getTime();
}

// ============================================================================
// Reglas de edición / eliminación / saldo
// ============================================================================

/**
 * Determina si los datos estructurales de la PO (monto, proveedor, fechas de
 * emisión, plazo, AFE, coste, PR origen) pueden editarse según el estado.
 *
 * Solo EMITIDA permite cambios estructurales libres. A partir de
 * EN_ESPERA_ENTREGA en adelante, los datos comerciales quedan congelados; solo
 * se editan los campos de recepción y pago (eso lo controla
 * `puedeEditarRecepcionPo`).
 */
export function puedeEditarDatosGeneralesPo(estado: EstadoPo): boolean {
  return estado === "EMITIDA";
}

/**
 * Determina si los campos de recepción/pago siguen editables (entrega real,
 * fecha de pago, valor de pago). Quedan abiertos hasta que la PO esté pagada o
 * cancelada.
 */
export function puedeEditarRecepcionPo(estado: EstadoPo): boolean {
  return estado !== "PAGADA" && estado !== "CANCELADA";
}

/**
 * Verifica si una PO puede eliminarse físicamente.
 *
 * Regla: solo se permite eliminar POs que aún no tienen huella financiera.
 * - PAGADA: no eliminable (pago ya registrado, requiere reverso financiero).
 * - ENTREGADA: no eliminable (recepción registrada, parte del flujo).
 * - VENCIDA: no eliminable (se debe gestionar como cancelación).
 * - EMITIDA / EN_ESPERA_ENTREGA / CANCELADA: eliminables.
 */
export function puedeEliminarPo(
  po: Pick<Po, "estado">,
): { puede: boolean; motivo?: string } {
  if (po.estado === "PAGADA") {
    return {
      puede: false,
      motivo: "No se puede eliminar una PO PAGADA (tiene flujo financiero registrado).",
    };
  }
  if (po.estado === "ENTREGADA") {
    return {
      puede: false,
      motivo: "No se puede eliminar una PO ENTREGADA (recepción registrada). Cancélala antes.",
    };
  }
  if (po.estado === "VENCIDA") {
    return {
      puede: false,
      motivo: "No se puede eliminar una PO VENCIDA. Gestionala como CANCELADA primero.",
    };
  }
  return { puede: true };
}

/**
 * Calcula el saldo disponible de una PR para emitir nuevas POs.
 *
 *   saldo = PR.monto − Σ(monto de POs no canceladas asociadas a la PR)
 *
 * Las POs canceladas no consumen saldo (se considera que se reemitirán).
 * Las POs PAGADA, ENTREGADA, EN_ESPERA, EMITIDA y VENCIDA sí consumen saldo.
 *
 * Si el saldo es ≤ 0 la PR no admite más POs (debe estar ADJ_TOTAL).
 */
export function saldoPrDisponible(
  pr: Pick<Po, "id"> & { montoUsd?: number },
  pos: Po[],
  excluirPoId?: string,
): { saldo: number; consumido: number } {
  // Cálculo puro en USD: PR.montoUsd − Σ PO.montoUsd. Sin conversiones runtime.
  const consumido = pos
    .filter(
      (p) =>
        p.prId === pr.id &&
        p.estado !== "CANCELADA" &&
        (excluirPoId === undefined || p.id !== excluirPoId),
    )
    .reduce((s, p) => s + (p.montoUsd ?? 0), 0);
  return {
    saldo: Math.max(0, (pr.montoUsd ?? 0) - consumido),
    consumido,
  };
}

/**
 * Valida que el monto de una PO no exceda el saldo disponible de la PR origen.
 * Comparación en USD canónico.
 */
export function validarMontoPoVsPr(
  po: Pick<Po, "id" | "montoUsd" | "prId">,
  prs: { id: string; montoUsd?: number }[],
  pos: Po[],
): { valido: boolean; saldoDisponible?: number; mensaje?: string } {
  if (!po.prId) return { valido: true };
  const pr = prs.find((p) => p.id === po.prId);
  if (!pr) return { valido: true };
  const { saldo } = saldoPrDisponible(pr, pos, po.id);
  if ((po.montoUsd ?? 0) > saldo + 0.01) {
    return {
      valido: false,
      saldoDisponible: saldo,
      mensaje: `El monto de la PO (USD ${(po.montoUsd ?? 0).toFixed(2)}) excede el saldo disponible de la PR (USD ${saldo.toFixed(2)}). Reduce el monto o emite una PR adicional.`,
    };
  }
  return { valido: true, saldoDisponible: saldo };
}

/** KPIs para el listado de POs. */
export function kpisPos(
  pos: Po[],
  trmPorAfe?: Map<string, number>,
): {
  total: number;
  emitidas: number;
  enEsperaEntrega: number;
  entregadas: number;
  pagadas: number;
  vencidas: number;
  canceladas: number;
  /** Monto agregado por moneda (no canceladas). */
  montoPorMoneda: Record<string, number>;
  /** Monto total convertido a USD usando TRM del AFE. */
  montoTotalUsd: number;
} {
  const activas = pos.filter((p) => p.estado !== "CANCELADA");
  const montoPorMoneda: Record<string, number> = {};
  let montoTotalUsd = 0;
  for (const p of activas) {
    montoPorMoneda[p.moneda] = (montoPorMoneda[p.moneda] ?? 0) + p.monto;
    if (p.moneda === "USD") {
      montoTotalUsd += p.monto;
    } else {
      const trm = trmPorAfe?.get(p.afeId);
      const trmEff = trm && trm > 0 ? trm : 4200;
      montoTotalUsd += p.monto / trmEff;
    }
  }
  return {
    total: pos.length,
    emitidas: pos.filter((p) => p.estado === "EMITIDA").length,
    enEsperaEntrega: pos.filter((p) => p.estado === "EN_ESPERA_ENTREGA").length,
    entregadas: pos.filter((p) => p.estado === "ENTREGADA").length,
    pagadas: pos.filter((p) => p.estado === "PAGADA").length,
    vencidas: pos.filter((p) => p.estado === "VENCIDA").length,
    canceladas: pos.filter((p) => p.estado === "CANCELADA").length,
    montoPorMoneda,
    montoTotalUsd: Math.round(montoTotalUsd * 100) / 100,
  };
}
